import React, { memo, useEffect, useRef, useState, RefObject } from 'react';
import {
  Accordion,
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { EpipProcessingStatusCode } from '../../../HbtPortfolioManagementPage/types';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import { HbtPortfolioManagementOverviewFlyout } from '../Flyout/HbtPortfolioManagementOverviewFlyout';
import { isRemarkOther } from '../../../../../Shared_Portfolio/utils/remarkOther';
import LoanStatusPill from '../HbtPortfolioLoansInventoryTable/LoanStatusPill';
import { errorSeverityMapping } from '../HbtPortfolioLoansInventoryTable/utils/ErrorSeverityMapping';
import { loanStatusMapping } from '../HbtPortfolioLoansInventoryTable/utils/LoanStatusMapping';
import { useLoansErrors } from '../RightPaneContent/hooks/useLoanErrors';
import { SeverityCode } from '../RightPaneContent/types';
import { ErrorListWithToast } from '../RightPaneContent/utils/copyItemsToClipBoard';
import errorsMapping from '../RightPaneContent/utils/errorsMapping';
import { groupErrorsBySeverity, sortSeverityKeys } from '../RightPaneContent/utils/filterDataUtils';
import { LayoutContainer } from './components/layoutContainer';
import { getIndividualLoanDetailsData } from './individualLoanDetailsService';
import { HbtIndividualLoanDetailsMapper } from './Mapper/HbtIndividualLoanDetailsMapper';
import styles from './styles.module.scss';
import {
  HbtPortfolioIndividualLoanDetailsProps,
  LoanDetails,
  LoanStatus,
  LoanStatusType
} from './types';
import { scrollToElement } from 'Feature/V2/Shared_Portfolio/utils/scrollToElement';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { useLocation } from 'react-router-dom';
import { clearSessionStorage } from 'Feature/V2/Shared_Portfolio/utils/clearSessionStorage';
import { renderEmptyDataState } from '../RightPaneContent/utils/renderEmptyStates';
import HistoricalDataFlyout from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolLoansInventoryTable/components/HistoricalDataFlyout/HistoricalDataFlyout';
import ForceStatusFlyout from 'Feature/V2/HbtMasterPool/components/HbtMasterPool/component/ForceStatusFlyout';
import { getRemarksDropDown } from '../HbtPortfolioLoansInventoryTable/utils/getRemarksDropDown';
import { config } from '../../../../../../../config';

export const handleScrollTo = (refKey: RefObject<HTMLDivElement>) => {
  setTimeout(() => {
    scrollToElement(refKey);
    clearSessionStorage('searchLoanDetail');
  }, 500);
};

const HbtPortfolioIndividualLoanDetails = ({
  portfolioId,
  loanReferenceNumber,
  loansId,
  latestRemarks,
  loanPortfolioEpipProcessingStatus,
  loanInventoryData,
  toggleToaster
}: HbtPortfolioIndividualLoanDetailsProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { t: mpd } = useTranslation('HbtMasterPool');
  const { data: loans } = useLoansErrors();
  const authenticationContext = useAuthenticationContext();
  const [loanDetails, setLoanDetails] = useState<LoanDetails>();
  const [isIndividualLoanDetailLoading, setIsIndividualLoanDetailLoading] = useState<boolean>(true);
  const [individualLoanDetailError, setIndividualLoanDetailError] = useState<string>('');

  const {
    borrowerLoanDetails = [],
    requestDetails,
    propertyDetails,
    keyLoanDetails,
    cmhcLowRatioDetails,
    otherLoanData
  } = (loanDetails && HbtIndividualLoanDetailsMapper(loanDetails)) || {};

  const [loanStatus, setLoanStatus] = useState<LoanStatusType>('04');

  const mappedCodes = loanInventoryData ? errorsMapping(loanInventoryData, loans ?? []) : undefined;

  const [isRequestDetailsCollapsed, setIsRequestDetailsCollapsed] = useState(true);
  const [isPropertyCollapsed, setIsPropertyCollapsed] = useState(false);
  const [isKeyLoanCollapsed, setIsKeyLoanCollapsed] = useState(false);
  const [isCmhcLowRatioCollapsed, setIsCmhcLowRatioCollapsed] = useState(false);
  const [isOtherLoanCollapsed, setIsOtherLoanCollapsed] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 736);
  const [openHistoricalFlyout, setOpenHistoricalFlyout] = React.useState<boolean>(false);
  const [showToaster, setShowToaster] = useState(false);

  const titleRef = useRef<HTMLDivElement | null>(null);
  const isAutoScrollingRef = useRef(false);
  const autoScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });

  const isReducedDataSet = loanDetails?.dataSetReducedIndicator === '1';

  const handleToastNotification = (icon: string, message: string, desc = '') => {
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };

  const location = useLocation();
  const { dataType } = getSearchParams(location.search);

  const handleToggle = () => {
    setCollapsed((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (!isTablet) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
      setIsDesktop(window.innerWidth >= 1024);
      setIsTablet(window.innerWidth >= 736);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isTablet]);

  useEffect(() => {
    if (isDesktop) return;

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      if (isAutoScrollingRef.current) {
        lastScrollY = window.scrollY;
        return;
      }

      const currentScrollY = window.scrollY;
      const isScrollingUp = currentScrollY < lastScrollY;
      const titleOffset = titleRef.current?.getBoundingClientRect().top || 0;

      if (isScrollingUp && titleOffset > 0) {
        setCollapsed(true);
      }

      if (!isScrollingUp && titleOffset === 0) {
        setCollapsed(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop]);

  const requestDetailsSectionPropDetails = {
    isCollapsed: isRequestDetailsCollapsed,
    onToggle: () => setIsRequestDetailsCollapsed(!isRequestDetailsCollapsed),
    lenderDetails: requestDetails
  };

  const propertyCharacteristicsSectionPropDetails = {
    className: styles.propertyContainer,
    isCollapsed: isPropertyCollapsed,
    onToggle: () => setIsPropertyCollapsed(!isPropertyCollapsed),
    propertyData: propertyDetails
  };

  const otherLoanCharacteristicsSectionPropDetails = {
    isCollapsed: isOtherLoanCollapsed,
    onToggle: () => setIsOtherLoanCollapsed(!isOtherLoanCollapsed),
    otherLoanData
  };
  const keyLoanCharacteristicsSectionPropDetails = {
    isCollapsed: isKeyLoanCollapsed,
    onToggle: () => setIsKeyLoanCollapsed(!isKeyLoanCollapsed),
    keyLoanData: keyLoanDetails,
    options: {
      isToolTip: true,
      toolTipDesc: (function () {
        if (keyLoanDetails?.grandfathered === '0') {
          return mpd('Grandfathered-Indicator-0');
        } else if (keyLoanDetails?.grandfathered === '1') {
          return mpd('Grandfathered-Indicator-1');
        }
        return mpd('Grandfathered-Indicator-2');
      })()
    }
  };
  const cmhcLowRatioCharacteristicsSectionPropDetails = {
    isCollapsed: isCmhcLowRatioCollapsed,
    onToggle: () => setIsCmhcLowRatioCollapsed(!isCmhcLowRatioCollapsed),
    cmhcLowRatioData: cmhcLowRatioDetails,
    options: {
      LayoutContainerType: 'validation review',
      isReducedDataSet: isReducedDataSet
    }
  };
  let pptCode: string | null = null;
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  pptCode = searchParams.get('pptCode');
  const data = loanInventoryData ? [loanInventoryData] : [];
  const { isCancellationLoan, remarksDropDownHeader } = getRemarksDropDown(data, pptCode);
  const loansUpdateUrl: string =
    config.validationReviewInventoryApi.urls.loanInventoryTabDetails + `/loans/decision-overrides`;

  const loanReviewTimestamp = loanDetails?.loanReviewTimestamp;
  const hasLoanReviewTimestamp =
    typeof loanReviewTimestamp === 'string' && loanReviewTimestamp.trim().length > 0;

  const formatToDateOnly = (rawTimestamp: string): string => {
    const [datePart] = rawTimestamp.split(' ');
    return datePart;
  };
  const [openOverviewFlyout, setOverviewFlyout] = useState(false);
  const toggleDrawer = (newOpen: boolean) => {
    setOverviewFlyout(newOpen);
    setFetchNewData(!fetchNewData);
  };
  const [openModifyFlyout, setModifyFlyout] = useState(false);
  const toggleModifyDrawer = (newOpen: boolean) => {
    setModifyFlyout(newOpen);
    setFetchNewData(!fetchNewData);
  };
  const [fetchNewData, setFetchNewData] = useState(true);

  const renderErrorsAccordion = () => {
    const severityMapping = errorSeverityMapping(t);

    let groupedErrors = groupErrorsBySeverity(mappedCodes ?? {});
    const severityKeys = Object.keys(groupedErrors) as SeverityCode[];

    if (severityKeys.length === 0) {
      return renderEmptyDataState(
        t('Pi-Portfolio-Management-Empty-Data-Error-Header'),
        t('PI-Portfolio-Management-Loans-No-Errors-SubBody')
      );
    }

    const sortedKeys = sortSeverityKeys(severityKeys, severityMapping);
    return (
      <Box className={styles?.contentAccordion}>
        {sortedKeys.map((severityKey) => (
          <Accordion
            key={severityKey}
            suppressBottomBorder={true}
            accordionData={{
              contentSlot: <ErrorListWithToast mappedErrors={groupedErrors[severityKey]!} />,
              contentType: 1,
              defaultExpanded: true,
              heading: `${severityMapping[severityKey]} (${
                Object.keys(groupedErrors[severityKey]!).length
              })`
            }}
          />
        ))}
      </Box>
    );
  };

  const fetchData = async (): Promise<LoanDetails | null> => {
    const data = await getIndividualLoanDetailsData({
      authenticationContext,
      data: {
        loansId
      }
    });
    return data;
  };

  const {
    data: loanDetailsData,
    isSuccess,
    isError,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['loan', fetchNewData, loansId],
    queryFn: fetchData,
    enabled: Boolean(loansId)
  });

  useEffect(() => {
    if (!isSuccess || !loanDetailsData) return;
    if (loanDetailsData) {
      setIndividualLoanDetailError('');
    } else {
      setIndividualLoanDetailError('no_data_error');
    }
    setLoanStatus(loanDetailsData.loanStatus as LoanStatusType);
    setLoanDetails(loanDetailsData);
    setIsIndividualLoanDetailLoading(false);
  }, [isSuccess, loanDetailsData as LoanDetails]);

  useEffect(() => {
    if (!isError) return;
    setIndividualLoanDetailError('api_error');
    setLoanDetails(undefined);
    setIsIndividualLoanDetailLoading(false);
  }, [isError]);

  useEffect(() => {
    if (!loanDetails || !titleRef.current) return;

    const hasLoanDetailSearch = sessionStorage.getItem('searchLoanDetail');

    if (loanDetailsData && dataType === 'Lender Reference Number' && hasLoanDetailSearch) {
      isAutoScrollingRef.current = true;

      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }

      handleScrollTo(titleRef);

      autoScrollTimeoutRef.current = setTimeout(() => {
        isAutoScrollingRef.current = false;
        autoScrollTimeoutRef.current = null;
      }, 900);
    }

    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
      isAutoScrollingRef.current = false;
    };
  }, [isSuccess, loanDetailsData, dataType, loanDetails]);

  return (
    <>
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>
      {loanInventoryData && (
        <>
          <ForceStatusFlyout
            openFlyout={openOverviewFlyout}
            toggleDrawer={toggleDrawer}
            loans={
              isCancellationLoan
                ? String(loanInventoryData.cmhcAccountNumber)
                : loanInventoryData.lenderReferenceNumber
            }
            loansData={loanInventoryData && [loanInventoryData]}
            radioButtonOptions={[
              {
                label: `${t('Passed')}`,
                value: `${LoanStatus.pass}`
              },
              {
                label: `${t('Failed')}`,
                value: `${LoanStatus.fail}`
              }
            ]}
            requestUrl={loansUpdateUrl}
            typeOfFlyout="updateLoan"
            headerText={
              isCancellationLoan
                ? t('Update-Loan-Header-Text-For-Cancellations')
                : t('Update-Loan-Header-Text')
            }
            loansToBeUpdatedTitle={
              isCancellationLoan ? undefined : t('Update-Loan-Loans-To-Be-Updated')
            }
            loansTypeToBeAdded={isCancellationLoan ? undefined : t('Update-Loan-To-Be-Added')}
            textFieldPlaceholder={
              isCancellationLoan ? undefined : t('Add-Loan-Textbox-Placeholder')
            }
            textFieldButtonText={
              isCancellationLoan ? undefined : t('Update-Loan-Text-Field-Button-Text')
            }
            addLoansDescription={
              isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Description')
            }
            addLoansTextFieldError={
              isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error')
            }
            toasterHeader={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Header')}
            toasterDesc={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Description')}
            addLoansTextFieldErrorTwo={
              isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error-Two')
            }
            remarksTextField={isCancellationLoan ? undefined : t('Update-Loan-Remark-Text-Field')}
            successFlyoutTitle={isCancellationLoan ? undefined : t('Confirmation-Flyout-Lender')}
            copyButtonText={isCancellationLoan ? undefined : t('Copy-Button')}
            copiedButtonText={isCancellationLoan ? undefined : t('Copied-Button')}
            overrideModalDesc={isCancellationLoan ? undefined : t('Override-Modal-Desc')}
            errorModalDesc={
              isCancellationLoan ? t('Error-Modal-Desc-Status-Cancellation') : t('Error-Modal-Desc')
            }
            typeOfPortfolioInsurance={'validationReview'}
            successFlyoutDesc={isCancellationLoan ? undefined : ' '}
            callToaster={handleToastNotification}
          />
          <ForceStatusFlyout
            callToaster={handleToastNotification}
            openFlyout={openModifyFlyout}
            toggleDrawer={toggleModifyDrawer}
            loans={
              isCancellationLoan
                ? String(loanInventoryData.cmhcAccountNumber)
                : loanInventoryData.lenderReferenceNumber
            }
            loansData={loanInventoryData && [loanInventoryData]}
            remarksDropDownHeader={remarksDropDownHeader}
            requestUrl={loansUpdateUrl}
            typeOfFlyout="updateRemark"
            flyoutTitle={t('Add-Remarks')}
            typeOfPortfolioInsurance={'validationReview'}
            headerText={t('Update-Remark-Header-Text')}
            loansToBeUpdatedTitle={
              isCancellationLoan ? undefined : t('Update-Loan-Loans-To-Be-Updated')
            }
            loansTypeToBeAdded={isCancellationLoan ? undefined : t('Update-Loan-To-Be-Added')}
            textFieldPlaceholder={
              isCancellationLoan ? undefined : t('Add-Loan-Textbox-Placeholder')
            }
            textFieldButtonText={
              isCancellationLoan ? undefined : t('Update-Loan-Text-Field-Button-Text')
            }
            addLoansDescription={
              isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Description')
            }
            addLoansTextFieldError={
              isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error')
            }
            toasterHeader={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Header')}
            toasterDesc={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Description')}
            addLoansTextFieldErrorTwo={
              isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error-Two')
            }
            remarksTextField={isCancellationLoan ? undefined : t('Update-Loan-Remark-Text-Field')}
            successFlyoutTitle={isCancellationLoan ? undefined : t('Confirmation-Flyout-Lender')}
            copyButtonText={isCancellationLoan ? undefined : t('Copy-Button')}
            copiedButtonText={isCancellationLoan ? undefined : t('Copied-Button')}
            overrideModalDesc={isCancellationLoan ? undefined : t('Override-Modal-Desc')}
            errorModalDesc={isCancellationLoan ? undefined : t('Error-Modal-Desc')}
            successFlyoutDesc={isCancellationLoan ? undefined : ' '}
          />
          {loanDetails && (
            <HistoricalDataFlyout
              openFlyout={openHistoricalFlyout}
              toggleFlyout={() => setOpenHistoricalFlyout(false)}
              selectedRowsData={[loanDetails]}
              isItMasterPoolLoans={false}
              isItLoanDetails
            />
          )}
        </>
      )}
      {isIndividualLoanDetailLoading && !individualLoanDetailError && <SpinnerElement />}
      {!isIndividualLoanDetailLoading && individualLoanDetailError && (
        <ErrorMessage errorType={individualLoanDetailError} />
      )}
      {loanDetails && (
        <Box className={styles.mainWrapper}>
          <Box className={styles.container}>
            <Box className={styles.boxShadowContainer}>
              <Box ref={titleRef} className={styles.titleContainer}>
                <Box className={styles.FirstRow}>
                  <div className={styles.portfolioId}>
                    {t('Portfolio-ID')}: {requestDetails?.portfolioId}
                  </div>
                  <Box className={styles.loanStatusContainer}>
                    <div className={styles.loanReferenceNumber}>{loanReferenceNumber}</div>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <LoanStatusPill
                        status={loanStatus}
                        filled={!hasLoanReviewTimestamp}
                        label={loanStatusMapping(t)[loanStatus]}
                      />
                      {hasLoanReviewTimestamp && (
                        <span className={styles.modifiedText}>({t('Modified')})</span>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box className={styles.SecondRow}>
                  {loanDetails?.remarks && loanDetails.remarks?.length > 0 && (
                    <Button
                      name="viewHistoricalData"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => {
                        setOpenHistoricalFlyout(true);
                      }}
                      text={{
                        value: t('View-Historical-Data')
                      }}
                    />
                  )}
                  {loanPortfolioEpipProcessingStatus ===
                    EpipProcessingStatusCode.VALIDATION_COMPLETED && (
                    <>
                      <Button
                        name="addModifyRemark"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        onClick={() => {
                          setModifyFlyout(true);
                        }}
                        text={{
                          value: t('Add-Remarks')
                        }}
                      />
                      <Button
                        name="OverrideLoanStatusBtn"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        style={{
                          backgroundColor: '#3F51B5',
                          color: '#fff',
                          textTransform: 'none'
                        }}
                        onClick={() => {
                          setOverviewFlyout(true);
                        }}
                        text={{
                          value: t('Update-Loan-Status')
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>

              {loanDetails.latestRemark && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: 'start',
                    alignItems: 'start'
                  }}
                  className={styles.remarkAddedContainer}
                >
                  <Tooltip
                    title={t('Remarks-Table-Header-Tooltip')}
                    placement="top-start"
                    arrow
                    slotProps={{
                      tooltip: {
                        className: styles.requestOverviewDetailsToolTip
                      },
                      arrow: {
                        className: styles.tooltipArrow
                      }
                    }}
                  >
                    <div>
                      <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                    </div>
                  </Tooltip>
                  <Stack direction="row" spacing={1} className={styles.remarksAdded}>
                    <Typography>
                      <span className={styles.remark}>
                        {t('PI-Portfolio-Management-Loans-Detail-Remark-Modified')}
                      </span>
                      <span className={styles.title}>
                        {t('PI-Portfolio-Management-Loans-Detail-Remark-Modified-Title', {
                          remark: isRemarkOther({
                            remarkID: loanDetails.latestRemark.remarkId,
                            remarkMessage: ''
                          })
                            ? loanDetails.latestRemark.remarkText
                            : i18n.language === 'en'
                              ? loanDetails.latestRemark.rationaleEnglish
                              : loanDetails.latestRemark.rationaleFrench
                        })}
                      </span>
                      <span className={styles.date}>
                        {t('PI-Portfolio-Management-Loans-Detail-Remark-Modified-Date', {
                          date: formatToDateOnly(loanDetails.latestRemark.recordTimestamp),
                          fName: loanDetails.latestRemark.firstName ?? '',
                          lName: loanDetails.latestRemark.lastName ?? ''
                        })}
                      </span>
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Box>

            {/* Main Content Container */}
            <Box className={styles.mainContentContainer}>
              {/* Layout Container */}
              <LayoutContainer
                borrowerDetails={borrowerLoanDetails}
                requestDetailsSectionPropDetails={requestDetailsSectionPropDetails}
                propertyCharacteristicsSectionPropDetails={
                  propertyCharacteristicsSectionPropDetails
                }
                otherLoanCharacteristicsSectionPropDetails={
                  otherLoanCharacteristicsSectionPropDetails
                }
                keyLoanCharacteristicsSectionPropDetails={keyLoanCharacteristicsSectionPropDetails}
                cmhcLowRatioCharacteristicsSectionPropDetails={
                  cmhcLowRatioCharacteristicsSectionPropDetails
                }
                options={{ LayoutContainerType: 'validation review' }}
              />
            </Box>
          </Box>
          {/* Right Side Panel */}
          {!collapsed ? (
            <Box className={styles.rightSidePanel}>
              <Box
                onClick={handleToggle}
                className={styles.toggleButton}
                aria-label={collapsed ? 'Expand Panel' : 'Collapse Panel'}
                sx={{
                  display: {
                    xs: 'flex', // visible on mobile
                    md: 'flex', // visible on tablet
                    lg: 'none' // hidden on desktop and up
                  }
                }}
              >
                <CmhcIcon
                  className={styles.accordionIcon}
                  size={CmhcIconSize.SMALL}
                  type={'icon_reduce'}
                />
              </Box>
              <Box className={styles.validationResultsContainer}>
                <Box className={styles.topBar}></Box>
                <Box className={styles.headerContainer}>
                  <Typography className={styles.headerTitle} variant="h6">
                    {t('Initial-Loan-Validation')}
                  </Typography>
                  <Typography className={styles.headerSubtitle} variant="h6">
                    {t('PI-Portfolio-Management-Loans-Details-SubHeader')}
                  </Typography>
                </Box>
                <Box className={styles?.contentAccordion}>{renderErrorsAccordion()}</Box>
              </Box>
            </Box>
          ) : (
            <Box className={styles.collapsedStub}>
              <Box
                onClick={handleToggle}
                className={styles.toggleButton}
                aria-label={collapsed ? 'Expand Panel' : 'Collapse Panel'}
              >
                <CmhcIcon
                  className={styles.accordionIcon}
                  size={CmhcIconSize.SMALL}
                  type={'icon_enlarge'}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default memo(HbtPortfolioIndividualLoanDetails);
